/**
 * Typography System
 *
 * Centralized typography utilities for consistent text styling across the application.
 * Uses Tailwind CSS class strings for easy integration with existing components.
 */

// ============================================================================
// Headings
// ============================================================================

/**
 * H1 - Page Title
 * Usage: Main page title (once per page)
 * Examples: "Agents", "Call Flows", "Dashboard"
 */
export const PAGE_TITLE = 'text-2xl font-semibold tracking-tight'

/**
 * H2 - Section Header
 * Usage: Major sections within a page, card titles
 * Examples: "Active Agents", "Recent Calls", card headers
 */
export const SECTION_HEADER = 'text-lg font-semibold'

/**
 * H3 - Subsection
 * Usage: Smaller groupings, form sections
 * Examples: "Contact Information", "Settings", form field groups
 */
export const SUBSECTION = 'text-base font-medium'

// ============================================================================
// Body Text
// ============================================================================

/**
 * Default Body Text
 * Usage: Primary content text, table cells, form inputs
 */
export const BODY_DEFAULT = 'text-sm'

/**
 * Muted Text
 * Usage: Secondary information, descriptions, helper text
 */
export const BODY_MUTED = 'text-sm text-muted-foreground'

/**
 * Small/Caption Text
 * Usage: Timestamps, metadata, table footers, input hints
 */
export const CAPTION = 'text-xs text-muted-foreground'

/**
 * Error Text
 * Usage: Inline error/failure messages at body size (e.g. a send/start
 * failure). Not CAPTION + text-destructive: CAPTION already bundles a muted
 * colour that would fight with the destructive one.
 */
export const ERROR_TEXT = 'text-sm text-destructive'

/**
 * Error Caption
 * Usage: Inline error/failure messages at caption size (e.g. a paginated
 * "load more" failure next to its retry button).
 */
export const ERROR_CAPTION = 'text-xs text-destructive'

// ============================================================================
// Data / Numeric Text
// ============================================================================

/**
 * Data Mono - Numeric & Identifier Text
 * Usage: Numeric stats, balances, timestamps, resource identifiers (call SID,
 * message ID) where column alignment and technical precision matter.
 * Examples: dashboard balance figures, call duration, resource IDs
 */
export const DATA_MONO = 'font-mono tabular-nums'
